/**
 * Base API client.
 *
 * All requests are made with credentials: 'include' so the session cookie is
 * sent automatically. On a 401 response the client redirects to /login.
 *
 * Non-2xx responses throw an ApiError with the HTTP status and message from
 * the response body (falls back to the status text).
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    window.location.href = '/login';
    // Throw so callers don't try to process the (empty/redirected) response
    throw new ApiError(401, 'Unauthenticated');
  }

  if (!response.ok) {
    let message = response.statusText;
    let retryAfterSeconds: number | undefined;
    try {
      const body = (await response.json()) as { error?: string; message?: string; retryAfterSeconds?: number };
      message = body.error ?? body.message ?? message;
      retryAfterSeconds = body.retryAfterSeconds;
    } catch {
      // ignore JSON parse errors — use status text
    }
    throw new ApiError(response.status, message, retryAfterSeconds);
  }

  // 204 No Content and 205 Reset Content carry no response body — skip JSON parsing
  if (response.status === 204 || response.status === 205) {
    return undefined as unknown as T;
  }
  return response.json() as Promise<T>;
}

export async function apiGet<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    method: 'GET',
    credentials: 'include',
  });
  return handleResponse<T>(response);
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    ...(body !== undefined
      ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      : {}),
  });
  return handleResponse<T>(response);
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method: 'PUT',
    credentials: 'include',
    ...(body !== undefined
      ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      : {}),
  });
  return handleResponse<T>(response);
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method: 'PATCH',
    credentials: 'include',
    ...(body !== undefined
      ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      : {}),
  });
  return handleResponse<T>(response);
}

export async function apiDelete<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    method: 'DELETE',
    credentials: 'include',
  });
  return handleResponse<T>(response);
}
